import React, { Component } from "react";
import PropTypes from "prop-types";
import { select, event as d3Event, mouse, touches } from "d3-selection";

import {
  isDefined,
  mousePosition,
  touchPosition,
  getTouchProps,
  d3Window,
  MOUSEMOVE,
  MOUSEUP,
  MOUSEENTER,
  MOUSELEAVE,
  TOUCHMOVE,
  TOUCHEND,
  noop,
} from "./utils";
import { getCurrentCharts } from "./utils/ChartDataUtil";
// import { clearTimeout, setTimeout } from "timers";

class EventCapture extends Component {
  constructor(props) {
    super(props);
    this.handleEnter = this.handleEnter.bind(this);
    this.handleLeave = this.handleLeave.bind(this);
    this.handleWheel = this.handleWheel.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handlePanEnd = this.handlePanEnd.bind(this);
    this.handlePan = this.handlePan.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handlePinchZoom = this.handlePinchZoom.bind(this);
    this.handlePinchZoomEnd = this.handlePinchZoomEnd.bind(this);

    this.handleClick = this.handleClick.bind(this);

    this.handleRightClick = this.handleRightClick.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragEnd = this.handleDragEnd.bind(this);
    this.handleTouchDrag = this.handleTouchDrag.bind(this);
    this.handleTouchDragEnd = this.handleTouchDragEnd.bind(this);

    this.shouldPan = this.shouldPan.bind(this);
    this.canPan = this.canPan.bind(this);

    this.setCursorClass = this.setCursorClass.bind(this);
    this.saveNode = this.saveNode.bind(this);

    this.handleInteractionCancel = this.handleInteractionCancel.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    this.mouseInside = false;

    this.mouseInteraction = true;

    // Throttle mouse move events to ~60fps to prevent UI jank during heavy load
    this.lastMouseMoveTime = 0;
    this.MOUSE_THROTTLE_MS = 16; // ~60fps

    // Touch gesture detection state
    this.touchStartTime = 0;
    this.touchStartPos = null;
    this.lastTapTime = 0;
    this.longPressTimer = null;
    this.LONG_PRESS_DELAY = 600; // ms for context menu
    this.DOUBLE_TAP_DELAY = 400; // ms between taps (slightly longer for touch)
    this.TAP_THRESHOLD = 10; // max px movement for tap
    this.DRAG_THRESHOLD = 10; // min px movement before drag starts
    this.touchDragStarted = false;
    this.touchDragInProgress = false;

    // Flag to prevent synthetic click events after touch events
    // On touch devices, a touch can generate both touchend AND a synthetic click
    // This flag prevents double-firing of click handlers
    this.preventSyntheticClick = false;

    // Tracks whether window focus-loss listeners are bound (idempotent across
    // the componentDidUpdate → componentDidMount re-run). See #337.
    this._focusListenersBound = false;

    this.state = {
      panInProgress: false,
    };
  }
  saveNode(node) {
    this.node = node;
  }
  UNSAFE_componentWillMount() {
    this.focus = this.props.focus;
  }
  componentDidMount() {
    if (this.node) {
      select(this.node)
        .on(MOUSEENTER, this.handleEnter)
        .on(MOUSELEAVE, this.handleLeave);

      // Recover from focus loss: when the pointer leaves to another OS
      // window/browser mid-pan, the MOUSEUP can be delivered elsewhere
      // (Windows-Chrome lost-mouseup), leaving panInProgress/panStart stuck —
      // which dead-locks wheel-zoom and pans erratically on return (#337).
      // Guarded so the componentDidUpdate re-run doesn't stack listeners.
      if (!this._focusListenersBound) {
        const win = d3Window(this.node);
        win.addEventListener("blur", this.handleInteractionCancel);
        win.document?.addEventListener(
          "visibilitychange",
          this.handleVisibilityChange,
        );
        this._focusListenersBound = true;
      }
    }
  }
  componentDidUpdate() {
    this.componentDidMount();
  }
  componentWillUnmount() {
    if (this.node) {
      select(this.node).on(MOUSEENTER, null).on(MOUSELEAVE, null);
      const win = d3Window(this.node);
      select(win).on(MOUSEMOVE, null);
      if (this._focusListenersBound) {
        win.removeEventListener("blur", this.handleInteractionCancel);
        win.document?.removeEventListener(
          "visibilitychange",
          this.handleVisibilityChange,
        );
        this._focusListenersBound = false;
      }
    }
    // Clean up long press timer
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    // Clean up pending pan-end timer
    if (isDefined(this.panEndTimeout)) {
      clearTimeout(this.panEndTimeout);
      this.panEndTimeout = undefined;
    }
  }
  handleEnter() {
    const e = d3Event;

    const { onMouseEnter } = this.props;
    this.mouseInside = true;
    if (!this.state.panInProgress && !this.state.dragInProgress) {
      const win = d3Window(this.node);
      select(win).on(MOUSEMOVE, this.handleMouseMove);
    }
    onMouseEnter(e);
  }
  handleLeave(e) {
    const { onMouseLeave } = this.props;
    this.mouseInside = false;
    if (!this.state.panInProgress && !this.state.dragInProgress) {
      const win = d3Window(this.node);
      select(win).on(MOUSEMOVE, null);
    }
    onMouseLeave(e);
  }
  handleWheel(e) {
    const { zoom, onZoom } = this.props;
    const { panInProgress } = this.state;

    const yZoom =
      Math.abs(e.deltaY) > Math.abs(e.deltaX) && Math.abs(e.deltaY) > 0;
    // const xPan = Math.abs(e.deltaY) < Math.abs(e.deltaX) && Math.abs(e.deltaX) > 0;
    const mouseXY = mousePosition(e);

    if (zoom && this.focus && yZoom && !panInProgress) {
      const zoomDir = e.deltaY > 0 ? 1 : -1;
      onZoom(zoomDir, mouseXY, e);
    } else if (this.focus) {
      if (this.shouldPan()) {
        // console.log("Do pan now...")
        // pan already in progress
        const { panStartXScale, chartsToPan } = this.state.panStart;
        this.lastNewPos = mouseXY;
        this.panHappened = true;

        this.dx += e.deltaX;
        this.dy += e.deltaY;
        const dxdy = { dx: this.dx, dy: this.dy };

        this.props.onPan(mouseXY, panStartXScale, dxdy, chartsToPan, e);
      } else {
        // console.log("Pan start...")
        // pan start

        const { xScale, chartConfig } = this.props;
        const currentCharts = getCurrentCharts(chartConfig, mouseXY);

        this.dx = 0;
        this.dy = 0;
        this.setState({
          panInProgress: true,
          panStart: {
            panStartXScale: xScale,
            panOrigin: mouseXY,
            chartsToPan: currentCharts,
          },
        });
      }
      this.queuePanEnd();
    }
  }
  queuePanEnd() {
    if (isDefined(this.panEndTimeout)) {
      clearTimeout(this.panEndTimeout);
    }
    this.panEndTimeout = setTimeout(() => {
      this.handlePanEnd();
    }, 100);
  }
  handleMouseMove() {
    const e = d3Event;

    // Skip if mouse move is inside a modal/dialog
    const target = e?.target;
    if (target && target.closest) {
      const isInModal =
        target.closest(".p-dialog") ||
        target.closest(".p-dialog-mask") ||
        target.closest(".modal-base") ||
        target.closest(".p-contextmenu");
      if (isInModal) {
        return;
      }
    }

    const { onMouseMove, mouseMove } = this.props;

    if (this.mouseInteraction && mouseMove && !this.state.panInProgress) {
      // Throttle mouse move events to ~60fps to prevent UI jank
      const now = performance.now();
      if (now - this.lastMouseMoveTime < this.MOUSE_THROTTLE_MS) {
        return;
      }
      this.lastMouseMoveTime = now;

      if (!this.node) return;

      let newPos;
      try {
        newPos = mouse(this.node);
      } catch {
        return;
      }

      onMouseMove(newPos, "mouse", e);
    }
  }
  handleClick(e) {
    // Skip if click is inside a modal/dialog
    const target = e.target;
    if (target && target.closest) {
      const isInModal =
        target.closest(".p-dialog") ||
        target.closest(".p-dialog-mask") ||
        target.closest(".modal-base") ||
        target.closest(".p-contextmenu");
      if (isInModal) {
        return;
      }
    }

    const mouseXY = mousePosition(e);
    const { onClick, onDoubleClick } = this.props;

    // Prevent synthetic click events after touch events (tablet/mobile double-fire issue)
    if (this.preventSyntheticClick) {
      this.preventSyntheticClick = false;
      return;
    }

    // Allow modifier key clicks (Shift/Ctrl/Meta) even if small pan movement occurred
    // This prevents accidental blocking of one-click trading due to touchpad sensitivity or mouse jitter
    const hasModifierKey = e.shiftKey || e.ctrlKey || e.metaKey;
    const shouldAllowClick = !this.panHappened && !this.dragHappened;
    const shouldAllowModifierClick = hasModifierKey && !this.dragHappened;

    if (shouldAllowClick || shouldAllowModifierClick) {
      if (this.clicked) {
        onDoubleClick(mouseXY, e);
        this.clicked = false;
      } else {
        onClick(mouseXY, e);
        this.clicked = true;
        setTimeout(() => {
          if (this.clicked) {
            this.clicked = false;
          }
        }, 400);
      }
    }
  }
  handleRightClick(e) {
    e.stopPropagation();
    e.preventDefault();

    // Skip if right-click is inside a modal/dialog
    const target = e.target;
    if (target && target.closest) {
      const isInModal =
        target.closest(".p-dialog") ||
        target.closest(".p-dialog-mask") ||
        target.closest(".modal-base") ||
        target.closest(".p-contextmenu");
      if (isInModal) {
        return;
      }
    }

    const { onContextMenu, onPanEnd } = this.props;

    const mouseXY = mousePosition(e, this.node.getBoundingClientRect());

    if (isDefined(this.state.panStart)) {
      const { panStartXScale, panOrigin, chartsToPan } = this.state.panStart;
      if (this.panHappened) {
        onPanEnd(mouseXY, panStartXScale, panOrigin, chartsToPan, e);
      }
      const win = d3Window(this.node);
      select(win).on(MOUSEMOVE, null).on(MOUSEUP, null);

      this.setState({
        panInProgress: false,
        panStart: null,
      });
    }

    onContextMenu(mouseXY, e);
  }

  handleDrag() {
    const e = d3Event;

    // Skip if drag is inside a modal/dialog
    const target = e?.target;
    if (target && target.closest) {
      const isInModal =
        target.closest(".p-dialog") ||
        target.closest(".p-dialog-mask") ||
        target.closest(".modal-base") ||
        target.closest(".p-contextmenu");
      if (isInModal) {
        return;
      }
    }

    if (this.props.onDrag) {
      this.dragHappened = true;
      if (!this.node) return;

      let mouseXY;
      try {
        mouseXY = mouse(this.node);
      } catch {
        return;
      }
      this.props.onDrag(
        {
          startPos: this.state.dragStartPosition,
          mouseXY,
        },
        e,
      );
    }
  }
  cancelDrag() {
    const win = d3Window(this.node);
    select(win)
      .on(MOUSEMOVE, this.mouseInside ? this.handleMouseMove : null)
      .on(MOUSEUP, null);

    this.setState({
      dragInProgress: false,
    });
    this.mouseInteraction = true;
  }
  handleDragEnd() {
    const e = d3Event;

    // Skip if dragEnd is inside a modal/dialog
    const target = e?.target;
    if (target && target.closest) {
      const isInModal =
        target.closest(".p-dialog") ||
        target.closest(".p-dialog-mask") ||
        target.closest(".modal-base") ||
        target.closest(".p-contextmenu");
      if (isInModal) {
        return;
      }
    }

    if (!this.node) return;
    let mouseXY;
    try {
      mouseXY = mouse(this.node);
    } catch {
      return;
    }

    const win = d3Window(this.node);
    select(win)
      .on(MOUSEMOVE, this.mouseInside ? this.handleMouseMove : null)
      .on(MOUSEUP, null);

    if (this.dragHappened) {
      this.props.onDragComplete(
        {
          mouseXY,
        },
        e,
      );
    }

    this.setState({
      dragInProgress: false,
    });
    this.mouseInteraction = true;
  }
  handleTouchDrag() {
    const e = d3Event;
    if (!this.node) return;

    // Skip if touch is inside a modal/dialog
    const target = e?.target;
    if (target && target.closest) {
      const isInModal =
        target.closest(".p-dialog") ||
        target.closest(".p-dialog-mask") ||
        target.closest(".modal-base") ||
        target.closest(".p-contextmenu");
      if (isInModal) {
        return;
      }
    }

    const touchList = touches(this.node);
    if (!touchList || touchList.length === 0) return;

    const touchXY = touchList[0];
    this.lastTouchPos = touchXY;

    // Check if we've moved enough to start dragging
    if (!this.touchDragInProgress && this.touchStartPos) {
      const dx = touchXY[0] - this.touchStartPos[0];
      const dy = touchXY[1] - this.touchStartPos[1];
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > this.DRAG_THRESHOLD) {
        this.touchDragInProgress = true;
        // Cancel long press since we're dragging
        if (this.longPressTimer) {
          clearTimeout(this.longPressTimer);
          this.longPressTimer = null;
        }
      }
    }

    if (this.props.onDrag && this.touchDragInProgress) {
      this.dragHappened = true;
      this.props.onDrag(
        {
          startPos: this.state.dragStartPosition,
          mouseXY: touchXY,
        },
        e,
      );
    }
  }
  handleTouchDragEnd() {
    const e = d3Event;
    if (!this.node) return;

    // Skip if touch is inside a modal/dialog
    const target = e?.target;
    if (target && target.closest) {
      const isInModal =
        target.closest(".p-dialog") ||
        target.closest(".p-dialog-mask") ||
        target.closest(".modal-base") ||
        target.closest(".p-contextmenu");
      if (isInModal) {
        return;
      }
    }

    const win = d3Window(this.node);
    select(win).on(TOUCHMOVE, null).on(TOUCHEND, null);

    if (this.dragHappened && this.touchDragInProgress) {
      // Get the last known position for dragComplete
      const touchXY = this.lastTouchPos || this.state.dragStartPosition;
      this.props.onDragComplete(
        {
          mouseXY: touchXY,
        },
        e,
      );
    } else if (!this.touchDragInProgress && this.touchStartPos) {
      // No drag happened - this was a tap on a selected object
      // Check for double-tap to trigger context menu
      const touchEndTime = Date.now();
      const touchDuration = touchEndTime - this.touchStartTime;
      const wasTap = touchDuration < 300;

      if (wasTap) {
        const touchXY = this.touchStartPos;
        const now = Date.now();

        if (now - this.lastTapTime < this.DOUBLE_TAP_DELAY) {
          // Double tap on selected object - trigger double click only
          // NOTE: Removed onContextMenu call to prevent interference with modals
          const { onDoubleClick } = this.props;
          if (onDoubleClick) {
            onDoubleClick(touchXY, e);
          }
          // Prevent synthetic click event after touch
          this.preventSyntheticClick = true;
          setTimeout(() => {
            this.preventSyntheticClick = false;
          }, 400);
          this.lastTapTime = 0;
        } else {
          // Single tap on selected object - just a click
          const { onClick } = this.props;
          if (onClick) {
            onClick(touchXY, e);
            // Prevent synthetic click event after touch
            this.preventSyntheticClick = true;
            setTimeout(() => {
              this.preventSyntheticClick = false;
            }, 400);
          }
          this.lastTapTime = now;
        }
      }
    }

    this.touchDragInProgress = false;
    this.touchDragStarted = false;
    // Reset touch state
    this.touchStartPos = null;
    this.touchStartTime = 0;
    this.setState({
      dragInProgress: false,
    });
    this.mouseInteraction = true;
  }
  canPan() {
    const { getAllPanConditions } = this.props;
    const { pan: initialPanEnabled } = this.props;

    const { panEnabled, draggable: somethingSelected } =
      getAllPanConditions().reduce(
        (returnObj, a) => {
          return {
            draggable: returnObj.draggable || a.draggable,
            panEnabled: returnObj.panEnabled && a.panEnabled,
          };
        },
        {
          draggable: false,
          panEnabled: initialPanEnabled,
        },
      );

    return {
      panEnabled,
      somethingSelected,
    };
  }
  handleMouseDown(e) {
    if (e.button !== 0) {
      return;
    }

    // Skip if click/mousedown is inside a modal/dialog (can happen on touch devices via synthetic events)
    const target = e.target;
    if (target && target.closest) {
      const isInModal =
        target.closest(".p-dialog") ||
        target.closest(".p-dialog-mask") ||
        target.closest(".modal-base") ||
        target.closest(".p-contextmenu");
      if (isInModal) {
        return;
      }
    }

    const { xScale, chartConfig, onMouseDown, onMouseMove } = this.props;

    this.panHappened = false;
    this.dragHappened = false;
    this.focus = true;

    if (!this.state.panInProgress && this.mouseInteraction) {
      const mouseXY = mousePosition(e);
      const currentCharts = getCurrentCharts(chartConfig, mouseXY);

      // Refresh hover states before checking canPan — same pattern as handleTouchStart (line 821).
      // Without this, throttled mousemove can leave stale hover=false, causing pan instead of drag.
      onMouseMove(mouseXY, "mouse", e, true);

      const { panEnabled, somethingSelected } = this.canPan();
      const pan = panEnabled && !somethingSelected;

      if (pan) {
        this.setState({
          panInProgress: pan,
          panStart: {
            panStartXScale: xScale,
            panOrigin: mouseXY,
            chartsToPan: currentCharts,
          },
        });

        const win = d3Window(this.node);
        select(win)
          .on(MOUSEMOVE, this.handlePan)
          .on(MOUSEUP, this.handlePanEnd);
      } else if (somethingSelected) {
        this.setState({
          panInProgress: false,
          dragInProgress: true,
          panStart: null,
          dragStartPosition: mouseXY,
        });
        this.props.onDragStart({ startPos: mouseXY }, e);
        // this.mouseInteraction = false;

        const win = d3Window(this.node);
        select(win)
          .on(MOUSEMOVE, this.handleDrag)
          .on(MOUSEUP, this.handleDragEnd);
      }

      onMouseDown(mouseXY, currentCharts, e);
    }
    e.preventDefault();
  }
  shouldPan() {
    const { pan: panEnabled, onPan } = this.props;
    return panEnabled && onPan && isDefined(this.state.panStart);
  }
  handlePan() {
    const e = d3Event;

    // Skip pan if chart resize is in progress
    if (window.chartResizeInProgress) {
      return;
    }

    if (this.shouldPan()) {
      const { panStartXScale, panOrigin, chartsToPan } = this.state.panStart;

      const mouseXY = this.mouseInteraction
        ? mouse(this.node)
        : touches(this.node)[0];

      this.lastNewPos = mouseXY;
      const dx = mouseXY[0] - panOrigin[0];
      const dy = mouseXY[1] - panOrigin[1];

      // Only mark as pan — and propagate onPan — when movement exceeds the
      // tap threshold. The previous shape called onPan on every jitter,
      // which set ChartCanvas.panInProgress=true; then handlePanEnd, gated by
      // panHappened, never called onPanEnd to clear it. Result: a short
      // click left the zoom path silently blocked (issue #337).
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > this.TAP_THRESHOLD) {
        this.panHappened = true;

        // Cancel long press timer on significant pan movement
        if (this.longPressTimer) {
          clearTimeout(this.longPressTimer);
          this.longPressTimer = null;
        }

        this.dx = dx;
        this.dy = dy;

        this.props.onPan(mouseXY, panStartXScale, { dx, dy }, chartsToPan, e);
      }
    }
  }
  handlePanEnd() {
    const e = d3Event;
    const { pan: panEnabled, onPanEnd } = this.props;

    if (isDefined(this.state.panStart)) {
      const { panStartXScale, chartsToPan } = this.state.panStart;

      const win = d3Window(this.node);
      select(win)
        .on(MOUSEMOVE, this.mouseInside ? this.handleMouseMove : null)
        .on(MOUSEUP, null)
        .on(TOUCHMOVE, null)
        .on(TOUCHEND, null);

      if (
        this.panHappened &&
        // && !this.contextMenuClicked
        panEnabled &&
        onPanEnd
      ) {
        const { dx, dy } = this;

        // console.log(dx, dy)
        delete this.dx;
        delete this.dy;
        onPanEnd(this.lastNewPos, panStartXScale, { dx, dy }, chartsToPan, e);
      }

      this.setState({
        panInProgress: false,
        panStart: null,
      });
    }
  }
  handleVisibilityChange() {
    if (!this.node) return;
    const { document: doc } = d3Window(this.node);
    // visibilitychange fires on both hide and re-show; only cancel on hide,
    // where a pending MOUSEUP/TOUCHEND can no longer arrive.
    if (doc && doc.hidden) {
      this.handleInteractionCancel();
    }
  }
  handleInteractionCancel() {
    // Nothing in progress → nothing to recover.
    if (
      !this.state.panInProgress &&
      !this.state.dragInProgress &&
      !isDefined(this.state.panStart)
    ) {
      return;
    }

    if (isDefined(this.panEndTimeout)) {
      clearTimeout(this.panEndTimeout);
      this.panEndTimeout = undefined;
    }
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    // Release the window-level drag/pan capture bound in handleMouseDown so
    // a later move can't replay a stale pan against the old panStart.
    if (this.node) {
      const win = d3Window(this.node);
      select(win)
        .on(MOUSEMOVE, this.mouseInside ? this.handleMouseMove : null)
        .on(MOUSEUP, null)
        .on(TOUCHMOVE, null)
        .on(TOUCHEND, null);
    }

    this.panHappened = false;
    this.dragHappened = false;
    delete this.dx;
    delete this.dy;

    this.setState({
      panInProgress: false,
      panStart: null,
      dragInProgress: false,
    });
  }
  handleTouchMove(e) {
    // Skip if a chart resize is in progress
    if (window.chartResizeInProgress) {
      return;
    }

    // Skip if touch is inside a modal/dialog
    const target = e.target;
    if (target && target.closest) {
      const isInModal =
        target.closest(".p-dialog") ||
        target.closest(".p-dialog-mask") ||
        target.closest(".modal-base") ||
        target.closest(".p-contextmenu");
      if (isInModal) {
        return;
      }
    }

    const { onMouseMove } = this.props;
    const touchXY = touchPosition(getTouchProps(e.touches[0]), e);

    // Track position for drag threshold detection and last position
    this.lastTouchPos = touchXY;

    // Check if we've moved enough to start dragging (if touch drag is pending)
    if (
      this.touchDragStarted &&
      !this.touchDragInProgress &&
      this.touchStartPos
    ) {
      const dx = touchXY[0] - this.touchStartPos[0];
      const dy = touchXY[1] - this.touchStartPos[1];
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > this.DRAG_THRESHOLD) {
        // Cancel long press since we're dragging
        if (this.longPressTimer) {
          clearTimeout(this.longPressTimer);
          this.longPressTimer = null;
        }
        this.touchDragInProgress = true;
      }
    }

    // Track movement for drawing mode completion
    // (drawing mode uses handleTouchMove instead of handlePan)
    if (this.touchStartPos) {
      const dx = touchXY[0] - this.touchStartPos[0];
      const dy = touchXY[1] - this.touchStartPos[1];
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > this.DRAG_THRESHOLD) {
        this.drawingMoved = true;
      }
    }

    onMouseMove(touchXY, "touch", e);
  }
  handleTouchStart(e) {
    this.mouseInteraction = false;

    // Skip if a chart resize is in progress (resize handle has priority)
    if (window.chartResizeInProgress) {
      return;
    }

    // Check if touch is on a resize handle element - let it handle the event
    const target = e.target;
    if (target && target.closest && target.closest(".chart-resize-handle")) {
      return;
    }

    // Skip if touch is inside a modal/dialog - let the modal handle the event
    // This prevents chart touch handlers from interfering with modal inputs
    if (target && target.closest) {
      const isInModal =
        target.closest(".p-dialog") ||
        target.closest(".p-dialog-mask") ||
        target.closest(".modal-base") ||
        target.closest(".p-contextmenu");
      if (isInModal) {
        return;
      }
    }

    // Also check by parent class for SVG elements (closest may not work in all SVG cases)
    let el = target;
    while (el && el !== this.node) {
      if (el.classList && el.classList.contains("chart-resize-handle")) {
        return;
      }
      el = el.parentElement;
    }

    const { pan: panEnabled, chartConfig, onMouseMove } = this.props;
    const { xScale, onPanEnd } = this.props;

    if (e.touches.length === 1) {
      // Prevent native touch behaviors (text selection, context menu, etc.)
      // This helps avoid Android intercepting long-press for text selection
      e.preventDefault();

      this.panHappened = false;
      this.dragHappened = false;
      this.touchDragStarted = false;
      this.touchDragInProgress = false;
      this.focus = true;

      const touchXY = touchPosition(getTouchProps(e.touches[0]), e);

      // Check if touch is in a resize handle zone (near bottom of chart sections)
      // Resize handles are positioned at height - 15 + 16 = height + 1, with 44px touch target
      const RESIZE_ZONE_HEIGHT = 30; // pixels from bottom of each chart section
      if (chartConfig && chartConfig.length > 1) {
        for (let i = 0; i < chartConfig.length - 1; i++) {
          const chart = chartConfig[i];
          if (chart && chart.origin && isDefined(chart.height)) {
            const chartBottom = chart.origin[1] + chart.height;
            const distance = Math.abs(touchXY[1] - chartBottom);

            // Check if touch Y is within resize zone (around the bottom of this chart)
            if (distance < RESIZE_ZONE_HEIGHT) {
              // Touch is in resize handle zone - trigger the resize directly
              if (window.__chartResizeHandlers) {
                // Try direct sectionIndex first, then search for composite keys (chartId_sectionIndex)
                let handler = window.__chartResizeHandlers.get(i);
                if (!handler) {
                  for (const [key, h] of window.__chartResizeHandlers) {
                    if (String(key).endsWith(`_${i}`)) {
                      handler = h;
                      break;
                    }
                  }
                }
                if (handler && handler.onStart) {
                  const touchY = e.touches[0].clientY + window.scrollY;
                  const started = handler.onStart(touchY);
                  if (started) {
                    return; // Let the resize handle track the touch
                  }
                }
              }
              // Fallback: still set flag to prevent pan
              window.chartResizeInProgress = true;
              return;
            }
          }
        }
      }

      // Store touch start info for gesture detection
      this.touchStartTime = Date.now();
      this.touchStartPos = touchXY;
      this.lastTouchPos = touchXY;

      // First, update mouse position so hover detection works
      onMouseMove(touchXY, "touch", e);

      // Check if something is selected and hovering (draggable)
      const { somethingSelected } = this.canPan();

      // Start long-press timer for context menu
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
      }
      this.longPressTimer = setTimeout(() => {
        // Only trigger context menu if we haven't moved much
        if (this.touchStartPos) {
          const currentPos = this.lastTouchPos || touchXY;
          const dx = currentPos[0] - this.touchStartPos[0];
          const dy = currentPos[1] - this.touchStartPos[1];
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < this.TAP_THRESHOLD) {
            // Trigger context menu (long-press)
            const { onContextMenu } = this.props;
            if (onContextMenu) {
              // Create a synthetic event
              const syntheticEvent = {
                ...e,
                preventDefault: () => {},
                stopPropagation: () => {},
              };
              onContextMenu(touchXY, syntheticEvent);
            }
            // Provide haptic feedback if available
            if (navigator.vibrate) {
              navigator.vibrate(50);
            }
          }
        }
        this.longPressTimer = null;
      }, this.LONG_PRESS_DELAY);

      if (somethingSelected) {
        // Start touch drag instead of pan
        this.touchDragStarted = true;

        this.setState({
          panInProgress: false,
          dragInProgress: true,
          panStart: null,
          dragStartPosition: touchXY,
        });
        this.props.onDragStart({ startPos: touchXY }, e);

        const win = d3Window(this.node);
        select(win)
          .on(TOUCHMOVE, this.handleTouchDrag, false)
          .on(TOUCHEND, this.handleTouchDragEnd, false);
      } else if (panEnabled) {
        const currentCharts = getCurrentCharts(chartConfig, touchXY);

        // Check if a drawing tool is active (disablePan = true on some component)
        const { panEnabled: drawingAllowsPan } = this.canPan();
        const isDrawingMode = !drawingAllowsPan;

        if (isDrawingMode) {
          // Drawing mode: track movement without panning the chart
          this.drawingMoved = false;
          this.drawingInProgress = true;
          this.setState({
            panInProgress: false,
            panStart: null,
          });

          // Use handleTouchMove for drawing (updates position without chart pan)
          const win = d3Window(this.node);
          select(win)
            .on(TOUCHMOVE, this.handleTouchMove, false)
            .on(TOUCHEND, this.handleTouchEnd, false);

          // Trigger onMouseDown to start the drawing
          const { onMouseDown } = this.props;
          if (onMouseDown) {
            onMouseDown(touchXY, currentCharts, e);
          }
        } else {
          // Normal pan mode: pan the chart while tracking
          this.setState({
            panInProgress: true,
            panStart: {
              panStartXScale: xScale,
              panOrigin: touchXY,
              chartsToPan: currentCharts,
            },
          });

          const win = d3Window(this.node);
          select(win)
            .on(TOUCHMOVE, this.handlePan, false)
            .on(TOUCHEND, this.handleTouchEnd, false);

          // Also trigger onMouseDown for any components that need it
          const { onMouseDown } = this.props;
          if (onMouseDown) {
            onMouseDown(touchXY, currentCharts, e);
          }
        }
      }
    } else if (e.touches.length === 2) {
      // Cancel long press on multi-touch
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }

      // pinch zoom begin
      const { panInProgress, panStart } = this.state;

      if (panInProgress && panEnabled && onPanEnd) {
        const { panStartXScale, panOrigin, chartsToPan } = panStart;

        const win = d3Window(this.node);
        select(win)
          .on(MOUSEMOVE, this.mouseInside ? this.handleMouseMove : null)
          .on(MOUSEUP, null)
          .on(TOUCHMOVE, this.handlePinchZoom, false)
          .on(TOUCHEND, this.handlePinchZoomEnd, false);

        const touch1Pos = touchPosition(getTouchProps(e.touches[0]), e);
        const touch2Pos = touchPosition(getTouchProps(e.touches[1]), e);

        if (this.panHappened && panEnabled && onPanEnd) {
          onPanEnd(this.lastNewPos, panStartXScale, panOrigin, chartsToPan, e);
        }

        this.setState({
          panInProgress: false,
          pinchZoomStart: {
            xScale,
            touch1Pos,
            touch2Pos,
            range: xScale.range(),
            chartsToPan,
          },
        });
      }
    }
  }
  handleTouchEnd(e) {
    // Skip if a chart resize is in progress
    if (window.chartResizeInProgress) {
      return;
    }

    // Skip if touch is inside a modal/dialog
    const target = e.target;
    if (target && target.closest) {
      const isInModal =
        target.closest(".p-dialog") ||
        target.closest(".p-dialog-mask") ||
        target.closest(".modal-base") ||
        target.closest(".p-contextmenu");
      if (isInModal) {
        return;
      }
    }

    // Cancel long press timer
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }

    // Skip if we're in drag mode - handleTouchDragEnd handles that case
    if (this.touchDragStarted || this.touchDragInProgress) {
      return;
    }

    const { onClick, onDoubleClick, onPanEnd, pan: panEnabled } = this.props;

    // Calculate if this was a tap (short touch without much movement)
    const touchEndTime = Date.now();
    const touchDuration = touchEndTime - this.touchStartTime;
    const wasTap =
      touchDuration < 300 &&
      !this.panHappened &&
      !this.drawingMoved &&
      this.touchStartPos;

    // Always clean up touch listeners (for both pan and drawing modes)
    const win = d3Window(this.node);
    select(win).on(TOUCHMOVE, null).on(TOUCHEND, null);

    // Handle pan end if pan was in progress
    if (isDefined(this.state.panStart)) {
      const { panStartXScale, chartsToPan } = this.state.panStart;

      if (this.panHappened && panEnabled && onPanEnd) {
        const { dx, dy } = this;
        delete this.dx;
        delete this.dy;
        onPanEnd(this.lastNewPos, panStartXScale, { dx, dy }, chartsToPan, e);
      }

      this.setState({
        panInProgress: false,
        panStart: null,
      });
    }

    // Handle drawing mode completion
    // In drawing mode, we use drawingMoved and lastTouchPos instead of panHappened and lastNewPos
    if (
      this.drawingInProgress &&
      this.drawingMoved &&
      this.lastTouchPos &&
      onClick
    ) {
      // Trigger onClick at the final position to complete the drawing
      onClick(this.lastTouchPos, e);
      // Prevent synthetic click event after touch
      this.preventSyntheticClick = true;
      setTimeout(() => {
        this.preventSyntheticClick = false;
      }, 400);
      this.drawingInProgress = false;
      this.drawingMoved = false;
    } else if (wasTap && this.touchStartPos) {
      // Handle tap gestures (click, double-click)
      const touchXY = this.touchStartPos;
      const now = Date.now();

      // Check for double-tap
      if (now - this.lastTapTime < this.DOUBLE_TAP_DELAY) {
        // Double tap detected
        if (onDoubleClick) {
          onDoubleClick(touchXY, e);
        }
        // NOTE: Removed onContextMenu call on double-tap to prevent interference with modals
        // Context menu is now only available via long-press
        // Prevent synthetic click event after touch
        this.preventSyntheticClick = true;
        setTimeout(() => {
          this.preventSyntheticClick = false;
        }, 400);
        this.lastTapTime = 0; // Reset to prevent triple-tap
      } else {
        // Single tap - trigger click
        if (onClick) {
          onClick(touchXY, e);
          // Prevent synthetic click event that browsers generate after touchend
          this.preventSyntheticClick = true;
          setTimeout(() => {
            this.preventSyntheticClick = false;
          }, 400);
        }
        this.lastTapTime = now;
      }
    } else if (this.panHappened && this.lastNewPos && onClick) {
      // For drag gestures (not taps), trigger onClick at the final position
      // This allows drawing tools to complete: touch-drag-lift should create a line
      // from start to end position, just like mouse click-drag-release
      //
      // Only do this if pan is disabled (a drawing tool is active)
      // to avoid accidentally selecting drawings when just panning the chart
      const { panEnabled: canPanEnabled } = this.canPan();
      if (!canPanEnabled) {
        onClick(this.lastNewPos, e);
        // Prevent synthetic click event after touch
        this.preventSyntheticClick = true;
        setTimeout(() => {
          this.preventSyntheticClick = false;
        }, 400);
      }
    }

    // Reset touch state
    this.touchStartPos = null;
    this.touchStartTime = 0;
    this.drawingInProgress = false;
    this.drawingMoved = false;
    this.mouseInteraction = true;
  }
  handlePinchZoom() {
    const e = d3Event;
    const [touch1Pos, touch2Pos] = touches(this.node);
    const { xScale, zoom: zoomEnabled, onPinchZoom } = this.props;

    // eslint-disable-next-line no-unused-vars
    const { chartsToPan, ...initialPinch } = this.state.pinchZoomStart;

    if (zoomEnabled && onPinchZoom) {
      onPinchZoom(
        initialPinch,
        {
          touch1Pos,
          touch2Pos,
          xScale,
        },
        e,
      );
    }
  }
  handlePinchZoomEnd() {
    const e = d3Event;

    const win = d3Window(this.node);
    select(win).on(TOUCHMOVE, null).on(TOUCHEND, null);

    const { zoom: zoomEnabled, onPinchZoomEnd } = this.props;

    // eslint-disable-next-line no-unused-vars
    const { chartsToPan, ...initialPinch } = this.state.pinchZoomStart;

    if (zoomEnabled && onPinchZoomEnd) {
      onPinchZoomEnd(initialPinch, e);
    }

    this.setState({
      pinchZoomStart: null,
    });
  }
  setCursorClass(cursorOverrideClass) {
    if (cursorOverrideClass !== this.state.cursorOverrideClass) {
      this.setState({
        cursorOverrideClass,
      });
    }
  }
  render() {
    const { height, width, disableInteraction, useCrossHairStyleCursor } =
      this.props;
    const className =
      this.state.cursorOverrideClass != null
        ? this.state.cursorOverrideClass
        : !useCrossHairStyleCursor
          ? ""
          : this.state.panInProgress
            ? "react-stockcharts-grabbing-cursor"
            : "react-stockcharts-crosshair-cursor";

    const interactionProps = disableInteraction || {
      onWheel: this.handleWheel,
      onMouseDown: this.handleMouseDown,
      onClick: this.handleClick,
      onContextMenu: this.handleRightClick,
      onTouchStart: this.handleTouchStart,
      onTouchMove: this.handleTouchMove,
      onTouchEnd: this.handleTouchEnd,
    };

    return (
      <rect
        ref={this.saveNode}
        className={className}
        width={width}
        height={height}
        style={{ opacity: 0, touchAction: "none" }}
        {...interactionProps}
      />
    );
  }
}

// 				onMouseEnter={this.handleEnter}
//				onMouseLeave={this.handleLeave}

EventCapture.propTypes = {
  mouseMove: PropTypes.bool.isRequired,
  zoom: PropTypes.bool.isRequired,
  pan: PropTypes.bool.isRequired,
  panSpeedMultiplier: PropTypes.number.isRequired,
  focus: PropTypes.bool.isRequired,
  useCrossHairStyleCursor: PropTypes.bool.isRequired,

  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  chartConfig: PropTypes.array,
  xScale: PropTypes.func.isRequired,
  xAccessor: PropTypes.func.isRequired,
  disableInteraction: PropTypes.bool.isRequired,

  getAllPanConditions: PropTypes.func.isRequired,

  onMouseMove: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onZoom: PropTypes.func,
  onPinchZoom: PropTypes.func,
  onPinchZoomEnd: PropTypes.func.isRequired,
  onPan: PropTypes.func,
  onPanEnd: PropTypes.func,
  onDragStart: PropTypes.func,
  onDrag: PropTypes.func,
  onDragComplete: PropTypes.func,

  onClick: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onContextMenu: PropTypes.func,
  onMouseDown: PropTypes.func,
  children: PropTypes.node,
};

EventCapture.defaultProps = {
  mouseMove: false,
  zoom: false,
  pan: false,
  panSpeedMultiplier: 1,
  focus: false,
  onDragComplete: noop,
  disableInteraction: false,
};

export default EventCapture;
